import classnames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import Button from '../../common/Button';
import { StartPrices } from '../../../../types/StartPrices';
import Lottie from '../../common/Lottie/Lottie';
import Loading from '../../../../public/animations/loading.json';
import displayInYen from '../../../core/displayInYen';
import {
  ContentType,
  DEFAULT_CHECK_IN_DATE,
  DEFAULT_CHECK_OUT_DATE,
  DEFAULT_GUEST_COUNT,
  MiniSiteTabOptions,
} from '../../../core/constants';
import analytics from '../../../core/analytics/analytics';
import { useSelector } from '../../../core/redux/store';
import useIsSticky from '../../../core/hooks/useIsSticky';
import { Hotel } from '../../../core/isValidHotel';

const TABS = 'tabs';

type Props = {
  currentTab: MiniSiteTabOptions;
  startPrices?: StartPrices;
  isStartPricesLoading: boolean;
  setIsModalOpen: (bool: boolean) => void;
  hotel?: Hotel;
};

const MiniSiteTabs = ({
  currentTab,
  startPrices,
  isStartPricesLoading,
  setIsModalOpen,
  hotel,
}: Props) => {
  const { t } = useTranslation(['hotel', 'common']);
  const router = useRouter();
  const { locale } = router;
  const { userAttributes } = useSelector((x) => x.account);
  const isLoggedIn = Boolean(userAttributes?.accountId);
  const [ref, isSticky] = useIsSticky<HTMLDivElement>({ offset: 64 }); // based on top-16 property

  const startPrice = startPrices?.startPrice;
  const discountedStartPrice =
    isLoggedIn && startPrices?.membersDiscountedStartPrice
      ? startPrices?.membersDiscountedStartPrice
      : startPrices?.discountedStartPrice;

  const renderPrice = useMemo(() => {
    if (isStartPricesLoading)
      return (
        <Lottie
          style={{ width: 85, height: '100%' }}
          params={{
            loop: true,
            animationData: Loading,
          }}
        />
      );
    if (!startPrice) return null;
    if (startPrice && !discountedStartPrice)
      return (
        <div className="flex items-center gap-4">
          <p className="sm:text-base">{t('hotel:hotel_general_info.price_for_adult_one_night')}</p>
          <span className="text-large font-bold leading-none">
            {displayInYen(Number(startPrice), locale)} ~
          </span>
        </div>
      );

    return (
      <>
        <p className="min-w-fit self-center text-base">
          {t('hotel:hotel_general_info.price_for_adult_one_night')}
        </p>
        <span className="min-w-fit self-center text-base leading-none text-gray-600 line-through">
          {displayInYen(Number(startPrice), locale)}
        </span>

        <div className="min-w-fit items-center self-center">
          <span className="text-2xl font-bold leading-none">
            {displayInYen(Number(discountedStartPrice), locale)} ~
          </span>
        </div>
      </>
    );
  }, [discountedStartPrice, isStartPricesLoading, locale, startPrice, t]);

  const events = {
    onClickTabItem: (tab: MiniSiteTabOptions) => {
      router
        .replace(
          {
            ...router,
            pathname: `/h/${router.query.hotel}`,
            query: {
              ...events.onGetQueryParameter(),
              tab,
            },
            hash: TABS,
          },
          undefined,
          { shallow: true, scroll: true }
        )
        .then(() => {
          // scroll up for the height of header
          window.scrollBy({ top: -64, behavior: 'instant' });
        });
    },
    onGetQueryParameter: () => {
      const parameters = router.query;
      return {
        checkInDate: parameters.checkInDate || parameters['checkin-date'] || DEFAULT_CHECK_IN_DATE,
        checkOutDate:
          parameters.checkOutDate || parameters['checkout-date'] || DEFAULT_CHECK_OUT_DATE,
        roomCount: parameters.roomCount || parameters['room-count'] || 1,
        guestPerRoom: parameters.guestPerRoom || parameters['guest-count'] || DEFAULT_GUEST_COUNT,
      };
    },
  };

  return (
    <div
      className="sticky top-14 z-10 mx-auto max-w-screen-xl sm:top-16 lg:px-8"
      ref={ref}
      id={TABS}
    >
      <div
        className={classnames(
          'flex h-12 items-center justify-between border-b border-gray-200 bg-white px-4 sm:h-16 sm:border',
          {
            'lg:rounded-lg': !isSticky,
            'lg:rounded-b-lg': isSticky,
          }
        )}
      >
        <div className="flex size-full sm:w-1/2">
          <button
            type="button"
            onClick={() => {
              if (currentTab !== MiniSiteTabOptions.HOTEL_DETAILS) {
                events.onClickTabItem(MiniSiteTabOptions.HOTEL_DETAILS);
                analytics.google.basic.track.select(ContentType.TAB, 'overview_tab');
              }
            }}
            className={classnames(
              'flex h-full w-1/2 min-w-fit items-center justify-center font-bold hover:cursor-pointer sm:w-1/3 lg:w-1/3',
              {
                'border-b-2 border-orange-700 text-orange-700':
                  currentTab === MiniSiteTabOptions.HOTEL_DETAILS,
              }
            )}
          >
            <p className="text-base">{t('mini_site.tab.overview')}</p>
          </button>
          <button
            type="button"
            onClick={() => {
              if (
                currentTab !== MiniSiteTabOptions.ROOMS ||
                router.pathname === '/b/[hotel]/configure'
              ) {
                events.onClickTabItem(MiniSiteTabOptions.ROOMS);
                analytics.google.basic.track.select(ContentType.TAB, 'room_and_plans_tab');
              }
            }}
            className={classnames(
              'flex h-full w-1/2 min-w-fit items-center justify-center font-bold hover:cursor-pointer sm:w-1/3 lg:w-1/3',
              {
                'border-b-2 border-orange-700 text-orange-700':
                  currentTab === MiniSiteTabOptions.ROOMS,
              }
            )}
          >
            <p className="text-bass">{t('mini_site.tab.plan_and_booking')}</p>
          </button>

          {isSticky && (
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(true);
                analytics.google.ecommerce.track.viewGallery(hotel?.id);
              }}
              className="flex h-full w-1/2 min-w-fit items-center justify-center font-bold hover:cursor-pointer sm:w-1/3 lg:w-1/3"
            >
              <p className="text-bass">{t('mini_site.tab.gallery')}</p>
            </button>
          )}
        </div>
        {currentTab !== MiniSiteTabOptions.ROOMS && (
          <div className="hidden space-x-4 sm:flex">
            {renderPrice}
            <Button
              color="red"
              className="flex grow px-4"
              onClick={() => events.onClickTabItem(MiniSiteTabOptions.ROOMS)}
            >
              {t('common:button.details_and_book')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniSiteTabs;
